import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaSync,
  FaFileUpload,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaLock,
  FaBuilding,
  FaUser,
  FaCalendarAlt,
  FaSearch,
  FaFileAlt,
  FaTimes,
  FaCloudDownloadAlt,
  FaFilter,
  FaStar,
  FaFolder,
  FaFolderOpen,
  FaHome,
  FaClock,
  FaChartBar,
  FaReceipt,
  FaMoneyBillWave,
  FaArrowLeft,
  FaSlidersH
} from "react-icons/fa";
import { LuFileSpreadsheet, LuLayers, LuRefreshCw, LuPlus, LuFileText } from "react-icons/lu";
import axios from "axios";
import PageHeader from "../../components/ui/PageHeader";
import SkeletonLoader from "../../components/ui/SkeletonLoader";
import { useToast } from "../../context/ToastContext";
import FinancialStatementViewer from "../../components/FinancialStatementViewer";

const FinancialReportsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // State variables
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  
  // Selected Report for Detail Viewing Mode ('center' table vs 'statement' viewer)
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Business Overview");

  // Persistent Favorites State in localStorage
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem("admin_financial_report_favorites");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      "Profit and Loss": true,
      "Balance Sheet": true,
      "def_pnl": true,
      "def_bs": true
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem("admin_financial_report_favorites", JSON.stringify(favorites));
    } catch (e) {}
  }, [favorites]);

  // Modals state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Sync Form State
  const [syncPeriod, setSyncPeriod] = useState("Year 2026-2027");
  const [syncOrgId, setSyncOrgId] = useState("60002462107");
  const [syncStartDate, setSyncStartDate] = useState("2026-04-01");
  const [syncEndDate, setSyncEndDate] = useState("2027-03-31");

  // Upload Form State
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadReportType, setUploadReportType] = useState("profit_and_loss");
  const [uploadCategory, setUploadCategory] = useState("Business Overview");
  const [uploadPeriod, setUploadPeriod] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPublish, setUploadPublish] = useState(true);

  // Filter & Search
  const [searchQuery, setSearchQuery] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // Categories list matching all 17 Zoho Books Reports Center categories
  const categoriesList = [
    { name: "Business Overview", icon: FaFolder },
    { name: "Sales", icon: FaChartBar },
    { name: "Inventory", icon: FaFolder },
    { name: "Inventory Valuation", icon: FaFolder },
    { name: "Receivables", icon: FaReceipt },
    { name: "Payments Received", icon: FaMoneyBillWave },
    { name: "Recurring Invoices", icon: FaReceipt },
    { name: "Payables", icon: FaMoneyBillWave },
    { name: "Purchases and Expenses", icon: FaFileAlt },
    { name: "Taxes", icon: FaFileAlt },
    { name: "Banking", icon: FaBuilding },
    { name: "Projects and Timesheet", icon: FaClock },
    { name: "Accountant", icon: FaBuilding },
    { name: "Budgets", icon: FaChartBar },
    { name: "Currency", icon: FaMoneyBillWave },
    { name: "Activity", icon: FaClock },
    { name: "Automation", icon: FaSlidersH }
  ];

  // 1. Fetch Users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("adminToken");
        if (!token) {
          navigate("/login");
          return;
        }
        const res = await axios.get(`${API_BASE_URL}/api/auth/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const fetchedUsers = res.data.users || res.data || [];
        setUsersList(fetchedUsers);

        if (userId) {
          const match = fetchedUsers.find((u) => u._id === userId);
          if (match) setSelectedUser(match);
          else if (fetchedUsers.length > 0) setSelectedUser(fetchedUsers[0]);
        } else if (fetchedUsers.length > 0) {
          setSelectedUser(fetchedUsers[0]);
        }
      } catch (err) {
        console.error("Failed to fetch users list", err);
        toast.error("Failed to load client accounts list.");
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [userId, navigate, API_BASE_URL]);

  // 2. Fetch Reports whenever selectedUser changes
  const fetchUserReports = async (targetUserId) => {
    if (!targetUserId) return;
    setLoadingReports(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API_BASE_URL}/api/auth/financial-reports/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const reports = res.data.reports || [];
      setReportsList(reports);
      setSelectedReport(null);
    } catch (err) {
      console.error("Failed to fetch financial reports", err);
      setReportsList([]);
      setSelectedReport(null);
      toast.error("Failed to fetch financial statements for selected client.");
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (selectedUser?._id) {
      fetchUserReports(selectedUser._id);
    }
  }, [selectedUser]);

  // Handle Client Switcher
  const handleSelectUser = (uId) => {
    const userObj = usersList.find((u) => u._id === uId);
    if (userObj) {
      setSelectedUser(userObj);
      navigate(`/client/${uId}/financial-reports`);
    }
  };

  // Helper to determine if a report is marked favorite
  const isReportFav = (rep) => {
    if (!rep) return false;
    if (rep._id && favorites[rep._id] !== undefined) {
      return !!favorites[rep._id];
    }
    if (rep.title && favorites[rep.title] !== undefined) {
      return !!favorites[rep.title];
    }
    // Default initial favorites matching Zoho Books screenshot (Profit and Loss & Balance Sheet)
    return rep.title === "Profit and Loss" || rep.title === "Balance Sheet" || rep._id === "def_pnl" || rep._id === "def_bs";
  };

  // Toggle Favorite
  const handleToggleFavorite = (rep, e) => {
    if (e) e.stopPropagation();
    if (!rep) return;
    const currentFav = isReportFav(rep);
    const newStatus = !currentFav;
    setFavorites(prev => {
      const updated = { ...prev };
      if (rep.title) updated[rep.title] = newStatus;
      if (rep._id) updated[rep._id] = newStatus;
      return updated;
    });
  };

  // Sync from Zoho Books (Direct Fetch All Data)
  const handleTriggerZohoSync = async () => {
    if (!selectedUser?._id) {
      toast.warning("Please select a client account first.");
      return;
    }
    setIsSyncing(true);

    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/financial-reports/sync-zoho`,
        {
          userId: selectedUser._id,
          organizationId: syncOrgId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        await fetchUserReports(selectedUser._id);
        toast.success(`Successfully fetched all Zoho Books reports for ${selectedUser.name || selectedUser.companyName || 'client'}!`);
      } else {
        toast.error(res.data.message || "Failed to sync with Zoho Books.");
      }
    } catch (err) {
      console.error("Zoho Sync error", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Zoho Books API Sync failed.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Upload Manual Report
  const handleUploadReport = async (e) => {
    e.preventDefault();
    if (!selectedUser?._id || !uploadFile) {
      toast.warning("Please select a file to upload.");
      return;
    }
    setIsUploading(true);

    try {
      const token = localStorage.getItem("adminToken");
      const formData = new FormData();
      formData.append("userId", selectedUser._id);
      formData.append("title", uploadTitle || "Financial Statement");
      formData.append("reportType", uploadReportType);
      formData.append("category", uploadCategory);
      formData.append("period", uploadPeriod);
      formData.append("publishedToUser", uploadPublish);
      formData.append("reportFile", uploadFile);

      const res = await axios.post(`${API_BASE_URL}/api/auth/financial-reports/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (res.data.success) {
        setShowUploadModal(false);
        setUploadTitle("");
        setUploadPeriod("");
        setUploadFile(null);
        await fetchUserReports(selectedUser._id);
        toast.success("Report uploaded successfully!");
      } else {
        toast.error(res.data.message || "Failed to upload report.");
      }
    } catch (err) {
      console.error("Upload report error", err);
      toast.error(err.response?.data?.error || "Failed to upload report.");
    } finally {
      setIsUploading(false);
    }
  };

  // Toggle Publish Status
  const handleTogglePublish = async (rep, currentStatus) => {
    const targetReportId = rep?.backendReportId || rep?._id;
    if (!targetReportId || String(targetReportId).startsWith("def_")) {
      toast.info("Default suite templates are visible by default.");
      return;
    }
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE_URL}/api/auth/financial-reports/toggle-publish`,
        {
          userId: selectedUser._id,
          reportId: targetReportId,
          publishedToUser: !currentStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUserReports(selectedUser._id);
      toast.info(currentStatus ? "Report hidden from client portal." : "Report published to client portal.");
    } catch (err) {
      console.error("Failed to toggle publish status", err);
      toast.error("Failed to update visibility status.");
    }
  };

  // Delete Report
  const handleDeleteReport = async (rep) => {
    const targetReportId = rep?.backendReportId || rep?._id;
    if (!targetReportId || String(targetReportId).startsWith("def_")) {
      toast.warning("Default template reports cannot be deleted.");
      return;
    }
    if (!window.confirm("Are you sure you want to permanently delete this report?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        `${API_BASE_URL}/api/auth/financial-reports/delete`,
        {
          userId: selectedUser._id,
          reportId: targetReportId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUserReports(selectedUser._id);
      if (selectedReport?._id === rep._id || selectedReport?.backendReportId === targetReportId) {
        setSelectedReport(null);
      }
      toast.success("Financial statement deleted.");
    } catch (err) {
      console.error("Failed to delete report", err);
      toast.error("Failed to delete statement.");
    }
  };

  // Standard Zoho Books Reports Center default suite template across all 17 categories
  const DEFAULT_REPORTS_SUITE = [
    // 1. Business Overview (10 Reports)
    { _id: 'def_pnl', title: 'Profit and Loss', category: 'Business Overview', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_pnl_sch3', title: 'Profit and Loss (Schedule III)', category: 'Business Overview', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_pnl_horiz', title: 'Horizontal Profit and Loss', category: 'Business Overview', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_cashflow', title: 'Cash Flow Statement', category: 'Business Overview', reportType: 'cash_flow_statement', createdBy: 'System Generated' },
    { _id: 'def_bs', title: 'Balance Sheet', category: 'Business Overview', reportType: 'balance_sheet', createdBy: 'System Generated' },
    { _id: 'def_bs_horiz', title: 'Horizontal Balance Sheet', category: 'Business Overview', reportType: 'balance_sheet', createdBy: 'System Generated' },
    { _id: 'def_bs_sch3', title: 'Balance Sheet (Schedule III)', category: 'Business Overview', reportType: 'balance_sheet', createdBy: 'System Generated' },
    { _id: 'def_ratios', title: 'Business Performance Ratios', category: 'Business Overview', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_forecasting', title: 'Cash Flow Forecasting', category: 'Business Overview', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_funds', title: 'Movement of Equity', category: 'Business Overview', reportType: 'balance_sheet', createdBy: 'System Generated' },

    // 2. Sales (5 Reports)
    { _id: 'def_sales_cust', title: 'Sales by Customer', category: 'Sales', reportType: 'sales_by_customer', createdBy: 'System Generated' },
    { _id: 'def_sales_item', title: 'Sales by Item', category: 'Sales', reportType: 'sales_by_item', createdBy: 'System Generated' },
    { _id: 'def_sales_person', title: 'Sales by Sales Person', category: 'Sales', reportType: 'sales_by_customer', createdBy: 'System Generated' },
    { _id: 'def_sales_return', title: 'Sales Return History', category: 'Sales', reportType: 'sales_by_customer', createdBy: 'System Generated' },
    { _id: 'def_packing_slips', title: 'Packing Slip Details', category: 'Sales', reportType: 'sales_by_customer', createdBy: 'System Generated' },

    // 3. Inventory (4 Reports)
    { _id: 'def_inv_summary', title: 'Inventory Summary', category: 'Inventory', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_inv_val_sum', title: 'Inventory Valuation Summary', category: 'Inventory', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_product_sales', title: 'Product Sales Report', category: 'Inventory', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_stock_wh', title: 'Stock Summary by Warehouse', category: 'Inventory', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 4. Inventory Valuation (3 Reports)
    { _id: 'def_fifo_tracking', title: 'FIFO Cost Lot Tracking', category: 'Inventory Valuation', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_inv_adj_hist', title: 'Inventory Adjustment History', category: 'Inventory Valuation', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_stock_val_det', title: 'Stock Valuation Details', category: 'Inventory Valuation', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 5. Receivables (11 Reports matching Zoho Books 1:1)
    { _id: 'def_rec_aging_sum', title: 'AR Aging Summary', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_aging_det', title: 'AR Aging Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_inv_det', title: 'Invoice Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_ret_det', title: 'Retainer Invoice Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_so_det', title: 'Sales Order Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_dc_det', title: 'Delivery Challan Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_est_det', title: 'Estimate Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_est_item', title: 'Estimate Item Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_cust_bal', title: 'Customer Balance Summary', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_summary', title: 'Receivable Summary', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_rec_details', title: 'Receivable Details', category: 'Receivables', reportType: 'receivables_aging', createdBy: 'System Generated' },

    // 6. Payments Received (3 Reports)
    { _id: 'def_pay_rec_hist', title: 'Payments Received', category: 'Payments Received', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_time_to_pay', title: 'Time to Pay by Customer', category: 'Payments Received', reportType: 'receivables_aging', createdBy: 'System Generated' },
    { _id: 'def_refund_hist', title: 'Refund History', category: 'Payments Received', reportType: 'receivables_aging', createdBy: 'System Generated' },

    // 7. Recurring Invoices (3 Reports)
    { _id: 'def_recurring_inv_det', title: 'Recurring Invoice Details', category: 'Recurring Invoices', reportType: 'sales_by_customer', createdBy: 'System Generated' },
    { _id: 'def_sub_summary', title: 'Subscriptions Summary', category: 'Recurring Invoices', reportType: 'sales_by_customer', createdBy: 'System Generated' },
    { _id: 'def_upcoming_rec', title: 'Upcoming Recurring Charges', category: 'Recurring Invoices', reportType: 'sales_by_customer', createdBy: 'System Generated' },

    // 8. Payables (12 Reports matching Zoho Books 1:1)
    { _id: 'def_pay_vend_bal', title: 'Vendor Balance Summary', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_pay_aging_sum', title: 'AP Aging Summary', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_pay_aging_det', title: 'AP Aging Details', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_bill_det', title: 'Bill Details', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_vc_det_pay', title: 'Vendor Credit Details', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_pay_made', title: 'Payments Made', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_refund_hist_pay', title: 'Refund History', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_po_det_pay', title: 'Purchase Order Details', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_po_by_vend', title: 'Purchase Orders by Vendor', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_po_by_item', title: 'Purchase Order By Item', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_pay_summary', title: 'Payable Summary', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_pay_details', title: 'Payable Details', category: 'Payables', reportType: 'payables_summary', createdBy: 'System Generated' },

    // 9. Purchases and Expenses (5 Reports)
    { _id: 'def_exp_det', title: 'Expense Details', category: 'Purchases and Expenses', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_exp_cat', title: 'Expenses by Category', category: 'Purchases and Expenses', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_exp_vend', title: 'Expenses by Vendor', category: 'Purchases and Expenses', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_po_det', title: 'Purchase Order Details', category: 'Purchases and Expenses', reportType: 'payables_summary', createdBy: 'System Generated' },
    { _id: 'def_vc_det', title: 'Vendor Credits Details', category: 'Purchases and Expenses', reportType: 'payables_summary', createdBy: 'System Generated' },

    // 10. Taxes (5 Reports)
    { _id: 'def_tax_sum', title: 'Tax Summary', category: 'Taxes', reportType: 'tax_summary', createdBy: 'System Generated' },
    { _id: 'def_gstr1', title: 'GSTR-1 Report', category: 'Taxes', reportType: 'tax_summary', createdBy: 'System Generated' },
    { _id: 'def_gstr3b', title: 'GSTR-3B Summary', category: 'Taxes', reportType: 'tax_summary', createdBy: 'System Generated' },
    { _id: 'def_tds_rep', title: 'TDS / Tax Deducted at Source', category: 'Taxes', reportType: 'tax_summary', createdBy: 'System Generated' },
    { _id: 'def_tax_audit_tr', title: 'Tax Audit Trail', category: 'Taxes', reportType: 'tax_summary', createdBy: 'System Generated' },

    // 11. Banking (4 Reports)
    { _id: 'def_bank_stmt', title: 'Bank Statements & Reconciliation', category: 'Banking', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_bank_cf', title: 'Cash Flow Statement (Bank)', category: 'Banking', reportType: 'cash_flow_statement', createdBy: 'System Generated' },
    { _id: 'def_uncl_chk', title: 'Uncleared Checks Report', category: 'Banking', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_acc_trx', title: 'Account Transactions Summary', category: 'Banking', reportType: 'trial_balance', createdBy: 'System Generated' },

    // 12. Projects and Timesheet (3 Reports)
    { _id: 'def_proj_sum', title: 'Project Summary', category: 'Projects and Timesheet', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_ts_det', title: 'Timesheet Details', category: 'Projects and Timesheet', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_unbilled_exp', title: 'Unbilled Hours & Expenses', category: 'Projects and Timesheet', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 13. Accountant (6 Reports)
    { _id: 'def_acc_trx_rep', title: 'Account Transactions', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_gen_ledger_rep', title: 'General Ledger', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_det_gl', title: 'Detailed General Ledger', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_trial_bal_rep', title: 'Trial Balance', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_journal_rep', title: 'Journal Report', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },
    { _id: 'def_day_book', title: 'Day Book', category: 'Accountant', reportType: 'trial_balance', createdBy: 'System Generated' },

    // 14. Budgets (3 Reports)
    { _id: 'def_bgt_vs_act', title: 'Budget vs Actuals', category: 'Budgets', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_bgt_perf', title: 'Budget Performance Summary', category: 'Budgets', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_fin_fc', title: 'Financial Forecast Comparison', category: 'Budgets', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 15. Currency (3 Reports)
    { _id: 'def_realized_gl', title: 'Realized Gain/Loss Report', category: 'Currency', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_unrealized_gl', title: 'Unrealized Gain/Loss Report', category: 'Currency', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_fx_hist', title: 'Exchange Rate History', category: 'Currency', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 16. Activity (3 Reports)
    { _id: 'def_sys_audit', title: 'System Audit Trail', category: 'Activity', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_usr_act', title: 'User Activity Logs', category: 'Activity', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_mail_hist', title: 'Mail History Report', category: 'Activity', reportType: 'profit_and_loss', createdBy: 'System Generated' },

    // 17. Automation (3 Reports)
    { _id: 'def_wf_logs', title: 'Workflow Logs', category: 'Automation', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_cf_hist', title: 'Custom Function Execution History', category: 'Automation', reportType: 'profit_and_loss', createdBy: 'System Generated' },
    { _id: 'def_wh_trig', title: 'Webhook Trigger Logs', category: 'Automation', reportType: 'profit_and_loss', createdBy: 'System Generated' }
  ];

  // Merge default suite with backend user reports
  const allReportsSuite = DEFAULT_REPORTS_SUITE.map(def => {
    const match = reportsList.find(r => 
      (r.title && r.title.toLowerCase().trim() === def.title.toLowerCase().trim()) ||
      (r.reportType && r.reportType === def.reportType && (r.category || 'Business Overview') === def.category)
    );

    if (match) {
      return {
        ...def,
        ...match,
        _id: def._id, // Preserve unique default suite item ID
        backendReportId: match._id,
        title: def.title
      };
    }

    // Default unvisited report template matching Zoho Books screenshot
    const fallbackReportData = reportsList.length > 0 ? (reportsList[0].reportData || {}) : {};
    return {
      ...def,
      publishedToUser: true,
      reportData: fallbackReportData,
      updatedAt: null
    };
  });

  // Include custom uploaded reports not in default template list
  reportsList.forEach(r => {
    if (!allReportsSuite.some(item => item._id === r._id || item.backendReportId === r._id)) {
      allReportsSuite.unshift({
        ...r,
        category: r.category || 'Business Overview'
      });
    }
  });

  // Filter reports based on Active Category, Search Query, Favorites
  const categoryReports = allReportsSuite.filter((rep) => {
    const repCategory = rep.category || "Business Overview";
    const isFav = isReportFav(rep);
    const matchesCategory = activeCategory === "Favorites"
      ? isFav
      : activeCategory === "Hidden Reports"
      ? rep.publishedToUser === false
      : activeCategory === "All Reports"
      ? true
      : repCategory === activeCategory;

    const matchesSearch =
      rep.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rep.period?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  if (loadingUsers) {
    return <SkeletonLoader variant="page" />;
  }

  return (
    <div className="space-y-6">
      {/* Standard Admin PageHeader */}
      <PageHeader
        title="Reports Center (Zoho Books)"
        description="Access and view all business overview, sales, receivables, taxes, and financial statements."
        icon={FaChartLine}
        iconGradient="from-blue-600 to-indigo-700"
        actions={
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={isSyncing}
              onClick={handleTriggerZohoSync}
              className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white text-sm font-semibold rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer disabled:opacity-50"
            >
              <FaSync className={`w-3.5 h-3.5 ${isSyncing ? "animate-spin" : ""}`} />
              <span>{isSyncing ? "Fetching All Reports..." : "Sync Zoho Reports"}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white text-sm font-semibold rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer"
            >
              <FaFileUpload className="w-3.5 h-3.5" />
              <span>Upload Custom Report</span>
            </button>
          </div>
        }
      />

      {/* Top Client Bar */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card p-4 border border-gray-100 dark:border-dark-700/50 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-[280px]">
          <div className="p-2.5 bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-xl">
            <FaUser className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <label className="block text-[11px] font-extrabold uppercase tracking-wider text-gray-400">
              Select Client / Business
            </label>
            <select
              value={selectedUser?._id || ""}
              onChange={(e) => handleSelectUser(e.target.value)}
              className="w-full bg-transparent text-sm font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
            >
              {usersList.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name || u.email} {u.companyName ? `(${u.companyName})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedUser && (
          <div className="flex items-center gap-3 text-xs font-semibold text-gray-600 dark:text-gray-300">
            <span className="px-3 py-1 bg-gray-100 dark:bg-dark-700 rounded-lg">
              Company: <strong>{selectedUser.companyName || "N/A"}</strong>
            </span>
            <span className="px-3 py-1 bg-gray-100 dark:bg-dark-700 rounded-lg font-mono">
              Org ID: <strong>{syncOrgId}</strong>
            </span>
          </div>
        )}
      </div>

      {/* If a report is currently selected to view in detail */}
      {selectedReport ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700/50 shadow-card">
            <button
              onClick={() => setSelectedReport(null)}
              className="px-4 py-2 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 text-gray-800 dark:text-gray-200 text-xs font-bold rounded-xl flex items-center gap-2 transition-all cursor-pointer"
            >
              <FaArrowLeft /> ← Back to Reports Center
            </button>
            <div className="text-right">
              <span className="text-xs text-gray-400 font-medium">Viewing Statement</span>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">{selectedReport.title}</h3>
            </div>
          </div>

          {selectedReport.fileUrl && !selectedReport.reportData ? (
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 border border-gray-100 dark:border-dark-700/50 shadow-card space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-700">
                <div>
                  <span className="px-2.5 py-1 bg-purple-50 text-purple-700 text-[10px] font-extrabold uppercase rounded-full">
                    Uploaded Document
                  </span>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                    {selectedReport.title}
                  </h2>
                </div>
                <a
                  href={`${API_BASE_URL}${selectedReport.fileUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-primary-600 text-white font-bold text-xs rounded-xl flex items-center gap-2"
                >
                  <FaCloudDownloadAlt /> Download File
                </a>
              </div>
              <div className="w-full h-[650px] bg-gray-100 rounded-2xl overflow-hidden">
                <iframe
                  src={`${API_BASE_URL}${selectedReport.fileUrl}`}
                  title={selectedReport.title}
                  className="w-full h-full"
                />
              </div>
            </div>
          ) : (
            <FinancialStatementViewer
              report={selectedReport}
              companyName={selectedUser?.companyName || selectedUser?.name}
              onClose={() => setSelectedReport(null)}
            />
          )}
        </div>
      ) : (
        /* Zoho Books Reports Center Layout */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Sidebar - Reports Center Categories */}
          <div className="lg:col-span-3 bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700/50 shadow-card overflow-hidden">
            <div className="p-4 bg-gray-50/70 dark:bg-dark-750 border-b border-gray-100 dark:border-dark-700 font-bold text-xs text-gray-400 uppercase tracking-wider">
              Reports Center Navigation
            </div>

            <div className="p-2 space-y-1">
              <button
                onClick={() => setActiveCategory("All Reports")}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                  activeCategory === "All Reports"
                    ? "bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                }`}
              >
                <FaHome className="w-4 h-4 text-blue-500" />
                <span>All Reports</span>
              </button>

              <button
                onClick={() => setActiveCategory("Favorites")}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                  activeCategory === "Favorites"
                    ? "bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <FaStar className="w-4 h-4 text-amber-500" />
                  <span>Favorites</span>
                </div>
                <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-extrabold">
                  {allReportsSuite.filter(r => isReportFav(r)).length}
                </span>
              </button>

              <button
                onClick={() => setActiveCategory("Hidden Reports")}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                  activeCategory === "Hidden Reports"
                    ? "bg-gray-200 dark:bg-dark-700 text-gray-900 dark:text-white"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <FaLock className="w-4 h-4 text-gray-500" />
                  <span>Hidden Reports</span>
                </div>
                <span className="text-[10px] bg-gray-200 text-gray-700 dark:bg-dark-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full font-extrabold">
                  {allReportsSuite.filter(r => r.publishedToUser === false).length}
                </span>
              </button>

              <div className="pt-3 pb-1 px-3 text-[10px] font-extrabold uppercase tracking-wider text-gray-400">
                REPORT CATEGORIES
              </div>

              {categoriesList.map((cat) => {
                const IconComp = cat.icon;
                const count = allReportsSuite.filter((r) => (r.category || "Business Overview") === cat.name).length;
                const isActive = activeCategory === cat.name;

                return (
                  <button
                    key={cat.name}
                    onClick={() => setActiveCategory(cat.name)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-bold transition-all text-left ${
                      isActive
                        ? "bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComp className={`w-4 h-4 ${isActive ? "text-primary-500" : "text-gray-400"}`} />
                      <span>{cat.name}</span>
                    </div>
                    {count > 0 && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          isActive
                            ? "bg-primary-100 text-primary-700 dark:bg-primary-900/60 dark:text-primary-300"
                            : "bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-400"
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Main Center Content Table */}
          <div className="lg:col-span-9 space-y-4">
            {/* Table Header Controls */}
            <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700/50 shadow-card flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  {activeCategory}
                </h2>
                <span className="px-2.5 py-0.5 bg-blue-50 dark:bg-blue-950/40 text-blue-600 text-xs font-extrabold rounded-full">
                  {categoryReports.length}
                </span>
              </div>

              {/* Search Bar matching Zoho Reports Center */}
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>

            {/* Reports Center Data Table */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700/50 shadow-card overflow-hidden">
              {loadingReports ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-xs text-gray-400 font-medium">Loading Reports Center...</p>
                </div>
              ) : categoryReports.length === 0 ? (
                <div className="p-12 text-center space-y-3">
                  <FaFileAlt className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto" />
                  <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    No reports found in {activeCategory}
                  </h4>
                  <p className="text-xs text-gray-400 max-w-sm mx-auto">
                    Click "Sync Zoho Reports" above to auto-generate all Profit & Loss, Balance Sheets, Cash Flow, and Tax statements.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50/80 dark:bg-dark-750 border-b border-gray-100 dark:border-dark-700 text-gray-400 uppercase tracking-wider text-[10px] font-extrabold">
                        <th className="py-3.5 px-4 w-10"></th>
                        <th className="py-3.5 px-4">REPORT NAME</th>
                        <th className="py-3.5 px-4">CREATED BY</th>
                        <th className="py-3.5 px-4">LAST VISITED / UPDATED</th>
                        <th className="py-3.5 px-4">CLIENT VISIBILITY</th>
                        <th className="py-3.5 px-4 text-right">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-750 text-xs">
                      {categoryReports.map((rep) => {
                        const isFav = isReportFav(rep);
                        return (
                          <tr
                            key={rep._id}
                            className="hover:bg-blue-50/30 dark:hover:bg-dark-700/50 transition-colors group cursor-pointer"
                            onClick={() => setSelectedReport(rep)}
                          >
                            {/* Star favorite toggle */}
                            <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={(e) => handleToggleFavorite(rep, e)}
                                className="text-gray-300 hover:text-amber-500 transition-colors cursor-pointer"
                                title={isFav ? "Remove from Favorites" : "Add to Favorites"}
                              >
                                <FaStar className={`w-4 h-4 ${isFav ? "text-amber-500" : ""}`} />
                              </button>
                            </td>

                            {/* Report Name */}
                            <td className="py-3.5 px-4">
                              <span className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                                {rep.title}
                              </span>
                              {rep.period && (
                                <span className="block text-[11px] text-gray-400 font-normal">
                                  Period: {rep.period}
                                </span>
                              )}
                            </td>

                            {/* Created By */}
                            <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300 font-medium">
                              {rep.createdBy || (rep.source === "zoho_sync" ? "System Generated" : "Manual Upload")}
                            </td>

                            {/* Last Visited / Updated */}
                            <td className="py-3.5 px-4 text-gray-500 font-mono text-[11px]">
                              {rep.updatedAt || rep.createdAt ? new Date(rep.updatedAt || rep.createdAt).toLocaleString() : "-"}
                            </td>

                            {/* Client Visibility Toggle */}
                            <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => handleTogglePublish(rep, rep.publishedToUser)}
                                className={`px-2.5 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-all ${
                                  rep.publishedToUser
                                    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200"
                                    : "bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-gray-400 hover:bg-gray-200"
                                }`}
                              >
                                {rep.publishedToUser ? "👁️ Published" : "🔒 Hidden"}
                              </button>
                            </td>

                            {/* Action Buttons */}
                            <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedReport(rep)}
                                  className="px-3 py-1.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 hover:bg-blue-100 font-bold rounded-lg text-xs transition-all flex items-center gap-1.5"
                                >
                                  <FaEye className="w-3 h-3" /> View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteReport(rep)}
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                                  title="Delete report"
                                >
                                  <FaTrash className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* MANUAL UPLOAD MODAL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-3xl max-w-md w-full p-6 border border-gray-200 dark:border-dark-700 shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400 rounded-xl">
                  <FaFileUpload className="w-4 h-4" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base">
                  Upload Custom Report
                </h3>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUploadReport} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  Report Title
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Horizontal P&L Statement 2026"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
                  >
                    <option value="Business Overview">Business Overview</option>
                    <option value="Sales">Sales</option>
                    <option value="Receivables">Receivables</option>
                    <option value="Payables">Payables</option>
                    <option value="Taxes">Taxes</option>
                    <option value="Accountant">Accountant</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Period
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Sept 2026"
                    value={uploadPeriod}
                    onChange={(e) => setUploadPeriod(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-xs font-bold text-gray-900 dark:text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  Report File (PDF / Word / Excel)
                </label>
                <input
                  type="file"
                  required
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="publishCheck"
                  checked={uploadPublish}
                  onChange={(e) => setUploadPublish(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                />
                <label
                  htmlFor="publishCheck"
                  className="text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  Publish to Client Portal immediately
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-emerald-500/25 flex items-center gap-2 disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <FaFileUpload className="w-3.5 h-3.5" />
                      <span>Upload & Save</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReportsPage;
